import { faUserTag } from '@fortawesome/pro-solid-svg-icons/faUserTag'
import { Text, Flex, Tooltip, Box } from '@mantine/core'
import { useRouter } from 'next/router'
import React from 'react'

import { useAnalytics } from '@flecto/analytics'
import { queryClient } from '@flecto/core'
import {
  useQueryGetBookingById,
  useMutationUpdateBooking,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreDiscountType } from '@flecto/types'
import { Badge, Icon } from '@flecto/ui'

export const DiscountLabel = ({ amount }: { amount: number }) => {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_sidebar' })
  const track = useAnalytics('segment')
  const router = useRouter()
  const id = Number(router.query.id)

  const { data, isLoading } = useQueryGetBookingById({ bookingId: id })
  const { mutateAsync } = useMutationUpdateBooking()

  const hasCustomDiscount = React.useMemo(() => {
    return (
      data?.discount_type !== data?.customer?.default_discount_type ||
      data?.discount_amount !== data?.customer?.default_discount_amount
    )
  }, [data])

  const renderAmount = React.useMemo(() => {
    const amountValue =
      data?.discount_type === 'discount_percentage'
        ? `${data?.discount_amount}%`
        : '€'

    return amountValue
  }, [amount])

  const handleReset = React.useCallback(async () => {
    const discountData = {
      bookingId: id,
      booking: {
        discount_type: data?.customer
          ?.default_discount_type as CoreDiscountType,
        discount_amount: data?.customer?.default_discount_amount,
      },
    }

    await mutateAsync(discountData)
    queryClient.invalidateQueries(['bookings', 'read', id])
    queryClient.invalidateQueries(['QueryGetBookingById', id])

    track('BOOKING__CUSTOMER_DISCOUNT_APPLIED', {
      booking_id: id,
      customerId: String(data?.customer?.id),
      discountType: discountData.booking.discount_type as CoreDiscountType,
      discountValue: Number(discountData.booking.discount_amount),
    })
  }, [data])

  const renderType = React.useMemo(() => {
    return data?.discount_type === 'discount_percentage' ? '%' : '€'
  }, [data])

  const hasDefaultDiscount = React.useMemo(() => {
    return Boolean(data?.customer?.default_discount_amount)
  }, [data])

  return (
    <Flex align={'baseline'} gap={'sm'}>
      <Text>
        {t('overview.discount')} (
        {!hasCustomDiscount ? renderType : renderAmount})
      </Text>
      {!isLoading && hasDefaultDiscount && (
        <Tooltip
          label={
            hasCustomDiscount
              ? t('overview.reset_discount')
              : t('overview.discount_tooltip', {
                  replace: {
                    amount: renderAmount,
                  },
                })
          }
          withinPortal
        >
          <Box onClick={hasCustomDiscount ? handleReset : undefined}>
            <Badge
              style={{ cursor: 'pointer' }}
              color={hasCustomDiscount ? 'gray' : 'green'}
              leftSection={<Icon icon={faUserTag} size='xs' />}
            >
              {hasCustomDiscount ? undefined : renderAmount}
            </Badge>
          </Box>
        </Tooltip>
      )}
    </Flex>
  )
}
